import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable, of as observableOf } from 'rxjs';
import {
  catchError,
  filter,
  map,
  mergeMap,
  withLatestFrom,
} from 'rxjs/operators';

import { State } from '®app/ngrx';
import { BibliographicResourceService } from '®shared/bibliographic-resource';
import { isLoadingOrLoaded } from '®shared/data';

import {
  FetchLibraryAvailabilityAction,
  FetchLibraryAvailabilityUnlessCachedAction,
  LibraryAvailabilityActionTypes,
  LibraryAvailabilityLoadedAction,
  LibraryAvailabilityLoadingFailedAction,
} from './library-availability.actions';
import {
  getLibraryAvailabilityForResourceId,
  selectLibraryAvailabilityState,
} from './library-availability.state';

@Injectable()
export class LibraryAvailabilityEffects {
  fetchLibraryAvailabilityUnlessCached$: Observable<FetchLibraryAvailabilityAction> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(
          LibraryAvailabilityActionTypes.LIBRARY_AVAILABILITY_REQUEST_UNLESS_CACHED,
        ),
        map(
          (action: FetchLibraryAvailabilityUnlessCachedAction) =>
            action.payload.resourceId,
        ),
        // only allow requests where the availability is neither already loaded, nor in the progress of loading
        withLatestFrom(
          this.store.select(selectLibraryAvailabilityState),
          (resourceId, state) => ({
            resourceId,
            availability:
              getLibraryAvailabilityForResourceId(resourceId)(state),
          }),
        ),
        filter(({ availability }) => !isLoadingOrLoaded(availability)),
        map(
          ({ resourceId }) =>
            new FetchLibraryAvailabilityAction({ resourceId }),
        ),
      ),
    );

  fetchLibraryAvailability$: Observable<
    LibraryAvailabilityLoadedAction | LibraryAvailabilityLoadingFailedAction
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(LibraryAvailabilityActionTypes.LIBRARY_AVAILABILITY_REQUESTED),
      map(
        (action: FetchLibraryAvailabilityAction) => action.payload.resourceId,
      ),
      mergeMap(resourceId =>
        this.bibliographicResourceService
          .getLibraryAvailabilityForResource(resourceId)
          .pipe(
            map(
              libraryAvailability =>
                new LibraryAvailabilityLoadedAction({
                  resourceId,
                  libraryAvailability,
                }),
            ),
            catchError(error =>
              observableOf(
                new LibraryAvailabilityLoadingFailedAction({
                  resourceId,
                  error,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private bibliographicResourceService: BibliographicResourceService,
    private store: Store<State>,
  ) {}
}
